import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { planContext } from "@/lib/plan";
import SignaturePad from "@/components/app/SignaturePad";
import { PenLine } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Save, Upload, X, Building2, Download, Trash2, ShieldAlert, UserCog, FileSpreadsheet, RefreshCw, CreditCard, Users, Mail, Plus, Clock } from "lucide-react";
import ImportDataPage from "@/pages/ImportData";

async function fileToDataUrl(file) {
  const r = new FileReader();
  return new Promise((res, rej) => { r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}

const empty = {
  name: "", company_name: "", address: "", postal_code: "", city: "",
  phone: "", siret: "", iban: "", logo_url: "", signature: "",
};

const SOURCE_LABELS = { whatsapp: "WhatsApp", instagram: "Instagram", facebook: "Facebook", google: "Google", web: "Web / autres" };

// --- Collaborateurs SCI (associé / comptable, lecture seule) -----------------
function CollaboratorsTab() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/account/members").then(({ data }) => setData(data)).catch(() => setData({ members: [], pending: [] }));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: r } = await api.post("/account/members/invite", { email, name });
      toast.success(r.email_sent ? `Invitation envoyée à ${r.email}` : `Invitation créée (email non envoyé) pour ${r.email}`);
      setEmail(""); setName("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const revokeMember = async (id) => {
    try { await api.delete(`/account/members/${id}`); toast.success("Collaborateur retiré"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const cancelInvite = async (id) => {
    try { await api.delete(`/account/members/invites/${id}`); toast.success("Invitation annulée"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const members = data?.members || [];
  const pending = data?.pending || [];

  return (
    <div className="space-y-6" data-testid="collaborators-tab">
      <Card className="p-6 border-stone-200 shadow-none">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-slate-900">Inviter un collaborateur</h3>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Partagez votre compte en <b>lecture seule</b> avec un associé de SCI ou votre comptable.
              Il pourra consulter biens, locataires, baux, quittances et finances, sans possibilité de modification.
            </p>
          </div>
        </div>
        <form onSubmit={invite} className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label>Nom du collaborateur</Label>
            <Input data-testid="member-invite-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ex : Jean Comptable" />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="member-invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="associe@exemple.fr" />
          </div>
          <Button data-testid="member-invite-submit" type="submit" disabled={busy} className="bg-[hsl(var(--primary))]">
            <Plus className="h-4 w-4 mr-2" /> {busy ? "Envoi…" : "Inviter"}
          </Button>
        </form>
      </Card>

      <Card className="p-6 border-stone-200 shadow-none">
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4">Collaborateurs du compte</div>
        {!data && <p className="text-sm text-slate-400">Chargement…</p>}
        {data && members.length === 0 && pending.length === 0 && (
          <p className="text-sm text-slate-400" data-testid="members-empty">Aucun collaborateur pour le moment.</p>
        )}
        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-3 border border-stone-200 rounded-lg px-4 py-3" data-testid={`member-row-${m.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate">{m.name}</div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-1"><Mail className="h-3 w-3" /> {m.email}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-teal-700 border-teal-200 bg-teal-50">Actif · lecture seule</Badge>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid={`member-revoke-${m.id}`} variant="ghost" size="icon" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Retirer ce collaborateur ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {m.name} ({m.email}) n'aura plus accès à votre compte. Vous pourrez le réinviter plus tard.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={() => revokeMember(m.id)} className="bg-rose-600 hover:bg-rose-700">Retirer</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
          {pending.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 border border-dashed border-stone-300 rounded-lg px-4 py-3 bg-slate-50/50" data-testid={`invite-row-${p.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate">{p.name}</div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-1"><Mail className="h-3 w-3" /> {p.email}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50"><Clock className="h-3 w-3 mr-1" /> Invitation en attente</Badge>
                <Button data-testid={`invite-cancel-${p.id}`} variant="ghost" size="icon" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => cancelInvite(p.id)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function TrafficCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState("");
  useEffect(() => {
    let on = true;
    api.get(`/admin/traffic?days=${days}`).then(({ data }) => { if (on) setData(data); }).catch(() => {});
    return () => { on = false; };
  }, [days]);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const periods = [[7, "7 j"], [30, "30 j"], [0, "Tout"]];
  const copy = (url, key) => { navigator.clipboard?.writeText(url); setCopied(key); setTimeout(() => setCopied(""), 1500); toast.success("Lien copié"); };
  return (
    <Card className="p-6 border-slate-200 shadow-none" data-testid="settings-traffic-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-heading font-bold text-slate-900">Provenance du trafic</h3>
        <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50">
          {periods.map(([d, lbl]) => (
            <button key={d} data-testid={`traffic-period-${d}`} onClick={() => setDays(d)}
              className={`px-3 py-1 text-xs rounded-md ${days === d ? "bg-white shadow-sm font-semibold text-slate-900" : "text-slate-500"}`}>{lbl}</button>
          ))}
        </div>
      </div>
      <p className="text-sm text-slate-500 mt-1">Visites et inscriptions selon l'origine des visiteurs.</p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="traffic-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2">Source</th><th className="py-2 text-right">Visites</th>
              <th className="py-2 text-right">Inscriptions</th><th className="py-2 text-right">Conversion</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.source} className="border-b border-slate-100" data-testid={`traffic-row-${r.source}`}>
                <td className="py-2 font-medium">{SOURCE_LABELS[r.source] || r.source}</td>
                <td className="py-2 text-right">{r.visits}</td>
                <td className="py-2 text-right">{r.signups}</td>
                <td className="py-2 text-right text-slate-500">{r.conversion}%</td>
              </tr>
            ))}
            {data && (
              <tr className="font-semibold" data-testid="traffic-row-total">
                <td className="py-2">Total</td>
                <td className="py-2 text-right">{data.totals.visits}</td>
                <td className="py-2 text-right">{data.totals.signups}</td>
                <td className="py-2 text-right text-slate-500">{data.totals.conversion}%</td>
              </tr>
            )}
          </tbody>
        </table>
        {!data && <p className="text-sm text-slate-400 py-4">Chargement…</p>}
      </div>

      <div className="mt-5">
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2">Liens à partager (provenance taguée)</div>
        <div className="space-y-2">
          {[["whatsapp", "WhatsApp"], ["instagram", "Instagram"], ["facebook", "Facebook"], ["google", "Google"]].map(([k, lbl]) => {
            const url = `${origin}/?src=${k}`;
            return (
              <div key={k} className="flex items-center gap-2 text-xs">
                <span className="w-20 shrink-0 text-slate-500">{lbl}</span>
                <code className="flex-1 min-w-0 truncate bg-slate-50 border border-slate-200 rounded px-2 py-1">{url}</code>
                <Button data-testid={`traffic-copy-${k}`} variant="outline" size="sm" onClick={() => copy(url, k)}>
                  {copied === k ? "Copié ✓" : "Copier"}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Partagez ces liens sur chaque réseau : chaque clic est alors compté dans la bonne source.</p>
      </div>
    </Card>
  );
}

export default function SettingsPage() {
  const { user, refresh, logout } = useAuth();
  const { canWrite, isDemo, isAdmin, tier } = planContext(user);
  const canManageMembers = isAdmin || ["patrimoine", "prestige"].includes(tier);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [syncingStripe, setSyncingStripe] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);

  const syncStripe = async () => {
    setSyncingStripe(true);
    try {
      const { data } = await api.post("/admin/stripe/sync");
      const mode = data.mode === "live" ? "PRODUCTION (live)" : "test";
      toast.success(`Catalogue Stripe synchronisé — mode ${mode} (${data.log?.length || 0} lignes)`);
    } catch (e) {
      toast.error("Erreur synchro Stripe : " + (e.response?.data?.detail || e.message));
    } finally { setSyncingStripe(false); }
  };

  const seedDemo = async () => {
    setSeedingDemo(true);
    try {
      await api.post("/admin/seed-demo");
      toast.success("Jeu de démo chargé — 7 biens réalistes. Rechargez la page.");
    } catch (e) {
      toast.error("Erreur chargement démo : " + (e.response?.data?.detail || e.message));
    } finally { setSeedingDemo(false); }
  };

  const exportRgpd = async () => {
    setExporting(true);
    try {
      const res = await api.get("/rgpd/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gestimmony-export-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export ZIP téléchargé");
    } catch (e) {
      toast.error("Erreur export : " + (e.response?.data?.detail || e.message));
    } finally { setExporting(false); }
  };

  const deleteAccount = async () => {
    if (!canWrite) return;
    setDeleting(true);
    try {
      await api.delete("/rgpd/account");
      toast.success("Compte supprimé");
      try { await logout(); } catch {}
      navigate("/");
    } catch (e) {
      toast.error("Erreur suppression : " + (e.response?.data?.detail || e.message));
    } finally { setDeleting(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/profile");
        setForm({ ...empty, ...data });
      } catch { /* keep empty */ }
    })();
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Logo trop lourd (>500 Ko)"); return; }
    const url = await fileToDataUrl(file);
    setForm({ ...form, logo_url: url });
  };

  const save = async () => {
    if (!canWrite) return;
    setBusy(true);
    try {
      await api.put("/settings/profile", form);
      toast.success("Paramètres enregistrés");
      await refresh();
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Paramètres</h1>
        <p className="mt-1 text-sm text-slate-500">
          Gérez votre profil bailleur, importez vos données et administrez votre compte.
        </p>
      </div>

      <Tabs defaultValue="profil" className="w-full">
        <TabsList className={`grid w-full h-auto ${canManageMembers ? "max-w-2xl grid-cols-4" : "max-w-xl grid-cols-3"}`} data-testid="settings-tabs">
          <TabsTrigger value="profil" className="gap-2 py-2" data-testid="settings-tab-profil">
            <UserCog className="h-4 w-4" /> Profil
          </TabsTrigger>
          {canManageMembers && (
            <TabsTrigger value="collaborateurs" className="gap-2 py-2" data-testid="settings-tab-collaborateurs">
              <Users className="h-4 w-4" /> Collaborateurs
            </TabsTrigger>
          )}
          <TabsTrigger value="import" className="gap-2 py-2" data-testid="settings-tab-import">
            <FileSpreadsheet className="h-4 w-4" /> Importer
          </TabsTrigger>
          <TabsTrigger value="rgpd" className="gap-2 py-2" data-testid="settings-tab-rgpd">
            <ShieldAlert className="h-4 w-4" /> RGPD
          </TabsTrigger>
        </TabsList>

        {/* ------------------------------ PROFIL ------------------------------ */}
        <TabsContent value="profil" className="space-y-6 mt-6">
          <p className="text-sm text-slate-500 -mb-2">
            Personnalisez les informations qui apparaissent en en-tête de vos <b>baux</b> et <b>quittances</b>.
          </p>

          {/* Preview */}
          <Card className="p-5 border-stone-200 shadow-none bg-[hsl(var(--accent))]/40">
            <div className="text-xs uppercase tracking-wider font-semibold text-[hsl(var(--primary))]">Aperçu en-tête PDF</div>
            <div className="mt-3 flex items-start gap-4">
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="h-16 w-16 object-contain bg-white rounded-lg border border-stone-200 p-1" />
              ) : (
                <div className="h-16 w-16 rounded-lg bg-white border border-dashed border-stone-300 flex items-center justify-center text-slate-400">
                  <Building2 className="h-5 w-5" />
                </div>
              )}
              <div className="text-sm leading-relaxed">
                <div className="font-semibold text-slate-900">{form.company_name || form.name || "Votre nom / raison sociale"}</div>
                {form.company_name && form.name && form.company_name !== form.name && (
                  <div className="text-slate-600">{form.name}</div>
                )}
                <div className="text-slate-600">
                  {[form.address, [form.postal_code, form.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "Adresse complète"}
                </div>
                <div className="text-slate-500 text-xs mt-0.5">
                  {[user?.email, form.phone].filter(Boolean).join(" · ")}
                </div>
                {form.siret && <div className="text-slate-500 text-xs">SIRET : {form.siret}</div>}
              </div>
            </div>
          </Card>

          {/* Identity */}
          <Card className="p-6 border-stone-200 shadow-none">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4">Identité du bailleur</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom complet du bailleur</Label>
                <Input data-testid="settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Raison sociale (SCI, entreprise) <span className="text-slate-400 text-xs">— optionnel</span></Label>
                <Input data-testid="settings-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Ex : SCI Immo Family" />
              </div>
              <div className="md:col-span-2">
                <Label>Adresse</Label>
                <Input data-testid="settings-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <Label>Code postal</Label>
                <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
              </div>
              <div>
                <Label>Ville</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input data-testid="settings-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>SIRET <span className="text-slate-400 text-xs">— si SCI/société</span></Label>
                <Input data-testid="settings-siret" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>IBAN pour paiement du loyer <span className="text-slate-400 text-xs">— optionnel, sera imprimé sur le bail</span></Label>
                <Input data-testid="settings-iban" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="FR76 3000 4000 5000 6000 7000 189" />
              </div>
            </div>
          </Card>

          {/* Logo */}
          <Card className="p-6 border-stone-200 shadow-none">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4">Logo (optionnel)</div>
            <div className="flex items-center gap-4">
              {form.logo_url && (
                <div className="relative">
                  <img src={form.logo_url} alt="logo" className="h-20 w-20 object-contain bg-white rounded-lg border border-stone-200 p-2" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, logo_url: "" })}
                    className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full h-5 w-5 flex items-center justify-center"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              <label className="inline-flex items-center gap-2 border border-stone-300 rounded-lg px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                <Upload className="h-4 w-4" />
                {form.logo_url ? "Remplacer" : "Uploader un logo"}
                <input type="file" accept="image/*" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="settings-logo-input" />
              </label>
              <div className="text-xs text-slate-500">PNG ou JPG, max 500 Ko. Sera intégré dans le PDF.</div>
            </div>
          </Card>

          {/* Signature du bailleur */}
          <Card className="p-6 border-stone-200 shadow-none">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Signature du bailleur (optionnel)</div>
            <p className="text-xs text-slate-500 mb-4">Votre signature manuscrite sera automatiquement ajoutée au bas des documents que vous émettez : quittances de loyer et courriers.</p>
            <div className="flex items-center gap-4">
              {form.signature ? (
                <div className="relative">
                  <img src={form.signature} alt="signature du bailleur" className="h-20 w-44 object-contain bg-white rounded-lg border border-stone-200 p-2" data-testid="settings-signature-preview" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, signature: "" })}
                    className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full h-5 w-5 flex items-center justify-center"
                    data-testid="settings-signature-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="h-20 w-44 rounded-lg border-2 border-dashed border-stone-300 flex items-center justify-center text-xs text-slate-400">
                  Aucune signature
                </div>
              )}
              <Button type="button" variant="outline" disabled={!canWrite} onClick={() => setSigOpen(true)} data-testid="settings-signature-btn">
                <PenLine className="h-4 w-4 mr-2" />
                {form.signature ? "Modifier la signature" : "Signer"}
              </Button>
            </div>
          </Card>

          <SignaturePad
            open={sigOpen}
            onOpenChange={setSigOpen}
            title="Votre signature"
            initialSignature={form.signature}
            onSave={(dataUrl) => setForm({ ...form, signature: dataUrl })}
          />

          {/* Account */}
          <Card className="p-6 border-stone-200 shadow-none">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4">Compte</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input value={user?.email || ""} readOnly className="bg-slate-50" />
              </div>
              <div>
                <Label>Rôle</Label>
                <div className="mt-1"><Badge variant="outline" className="capitalize">{user?.role}</Badge></div>
              </div>
            </div>
          </Card>

          {user?.role === "admin" && <TrafficCard />}

          {/* Catalogue Stripe — admin uniquement */}
          {user?.role === "admin" && (
            <Card className="p-6 border-teal-200 shadow-none bg-teal-50/30" data-testid="settings-admin-card">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
                  <CreditCard className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-teal-700">Administration</div>
                  <h3 className="font-heading font-bold text-slate-900 mt-0.5">Catalogue Stripe</h3>
                  <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                    Crée ou met à jour les 3 formules et leurs <b>6 prix (mensuel + annuel)</b> sur votre compte Stripe,
                    et désactive les anciens tarifs. <b>À lancer une fois en production</b> si une formule
                    (ex. offre annuelle) apparaît comme « indisponible » au paiement. Opération sans risque (idempotente) ;
                    en production elle s'applique à votre <b>compte Stripe live</b>.
                  </p>
                  <Button
                    data-testid="admin-stripe-sync-btn"
                    onClick={syncStripe}
                    disabled={syncingStripe}
                    variant="outline"
                    className="mt-3 border-teal-300"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${syncingStripe ? "animate-spin" : ""}`} />
                    {syncingStripe ? "Synchronisation…" : "Synchroniser le catalogue Stripe"}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Jeu de démo — admin uniquement */}
          {user?.role === "admin" && (
            <Card className="p-6 border-teal-200 shadow-none bg-teal-50/30" data-testid="settings-demo-card">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-teal-700">Administration</div>
                  <h3 className="font-heading font-bold text-slate-900 mt-0.5">Jeu de démo</h3>
                  <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                    Charge (ou réinitialise) un portefeuille <b>Patrimoine réaliste</b> sur ce compte :
                    7 biens, locataires, baux, quittances et incidents. Idéal pour vos démos commerciales.
                    <b> Remplace les données actuelles de ce compte</b> (opération sans risque, rejouable).
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        data-testid="admin-seed-demo-btn"
                        disabled={seedingDemo}
                        variant="outline"
                        className="mt-3 border-teal-300"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${seedingDemo ? "animate-spin" : ""}`} />
                        {seedingDemo ? "Chargement…" : "Charger un jeu de démo"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Charger le jeu de démo ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Les biens, locataires, baux, quittances et incidents actuels de ce compte seront
                          remplacés par le portefeuille de démonstration (7 biens). Cette action est
                          irréversible mais rejouable à volonté.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction data-testid="admin-seed-demo-confirm" onClick={seedDemo}>
                          Confirmer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          )}

          <div className="flex justify-end sticky bottom-4">
            <Button data-testid="settings-save" disabled={busy || !canWrite} onClick={save} className="bg-[hsl(var(--primary))] shadow-lg">
              <Save className="h-4 w-4 mr-2" /> {busy ? "Enregistrement…" : "Enregistrer les modifications"}
            </Button>
          </div>
        </TabsContent>

        {/* -------------------------- COLLABORATEURS -------------------------- */}
        {canManageMembers && (
          <TabsContent value="collaborateurs" className="mt-6">
            <CollaboratorsTab />
          </TabsContent>
        )}

        {/* ------------------------------ IMPORT ------------------------------ */}
        <TabsContent value="import" className="mt-6">
          <ImportDataPage />
        </TabsContent>

        {/* ------------------------------ RGPD ------------------------------ */}
        <TabsContent value="rgpd" className="mt-6">
          <Card className="p-6 border-rose-200 shadow-none bg-rose-50/30" data-testid="settings-rgpd-card">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-rose-700">RGPD — Vos données</div>
                <h3 className="font-heading font-bold text-slate-900 mt-0.5">Portabilité & droit à l'oubli</h3>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  Conformément aux articles 15, 17 et 20 du RGPD, vous pouvez récupérer l'intégralité de vos données à tout
                  moment ou supprimer définitivement votre compte.
                </p>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <div className="border border-stone-200 rounded-xl p-4 bg-white">
                <div className="flex items-center gap-2 mb-1">
                  <Download className="h-4 w-4 text-teal-700" />
                  <div className="font-heading font-semibold text-slate-900 text-sm">Exporter mes données</div>
                </div>
                <p className="text-xs text-slate-500">
                  Télécharge un ZIP contenant tous vos biens, locataires, baux, quittances, EDL et incidents au format JSON.
                </p>
                <Button
                  data-testid="rgpd-export-btn"
                  onClick={exportRgpd}
                  disabled={exporting}
                  variant="outline"
                  className="mt-3 w-full"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exporting ? "Génération..." : "Télécharger l'export ZIP"}
                </Button>
              </div>

              <div className="border border-rose-200 rounded-xl p-4 bg-white" style={{ display: isDemo ? "none" : undefined }}>
                <div className="flex items-center gap-2 mb-1">
                  <Trash2 className="h-4 w-4 text-rose-600" />
                  <div className="font-heading font-semibold text-slate-900 text-sm">Supprimer mon compte</div>
                </div>
                <p className="text-xs text-slate-500">
                  Suppression <b>irréversible</b> du compte et de toutes les données rattachées.
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      data-testid="rgpd-delete-btn"
                      variant="destructive"
                      className="mt-3 w-full"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Supprimer définitivement
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="rgpd-delete-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmer la suppression du compte</AlertDialogTitle>
                      <AlertDialogDescription>
                        Cette action est <b>définitive</b>. Toutes vos données (biens, locataires, baux, quittances,
                        EDL, incidents) seront effacées immédiatement et ne pourront pas être récupérées.
                        <br /><br />
                        Pour confirmer, saisissez <b>SUPPRIMER</b> ci-dessous.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <Input
                      data-testid="rgpd-delete-confirm-input"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="SUPPRIMER"
                    />
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => setConfirmText("")} data-testid="rgpd-delete-cancel">
                        Annuler
                      </AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="rgpd-delete-confirm"
                        disabled={confirmText !== "SUPPRIMER" || deleting}
                        onClick={deleteAccount}
                        className="bg-rose-600 hover:bg-rose-700"
                      >
                        {deleting ? "Suppression..." : "Oui, supprimer mon compte"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
